import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api';
import { t, locale } from '../i18n';
import { Card, Button, Field, Input, Textarea, Select, SectionTitle, Spinner, Flash } from '../ui';
import PhotoManager from '../components/PhotoManager';
import LocationEditor from '../components/LocationEditor';
import OpeningPeriod from '../components/OpeningPeriod';

const LANGS = ['fr', 'en', 'de', 'es', 'it', 'nl'];
const FLAG = (code) => `/img/flags/${code === 'en' ? 'gb' : code}.svg`;

const SERVICES = [
    'animaux', 'point_eau', 'eau_noire', 'eau_usee', 'poubelle', 'wc_public',
    'douche', 'boulangerie', 'electricite', 'wifi', 'piscine', 'laverie',
];
const ACTIVITIES = [
    'visites', 'vtt', 'rando', 'windsurf', 'escalade', 'eaux_vives', 'peche',
    'peche_pied', 'baignade', 'moto', 'point_de_vue', 'jeux_enfants',
];
const BORNES = ['', 'S', 'A', 'AS', 'AU', 'ER', 'FB', 'P', 'RA', 'U'];
// 0.00 = pas de limite, puis 1.8 m → 4.0 m
const HEIGHTS = ['0.00', ...Array.from({ length: 23 }, (_, i) => ((i + 18) / 10).toFixed(2))];

/** Case à cocher qui force une valeur spéciale (gratuit / all_year). */
function SpecialValueCheckbox({ label, value, special, onChange }) {
    const checked = value === special;
    return (
        <label className="checkbox-row text-muted" style={{ marginTop: 6 }}>
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked ? special : '')}
            />
            {label}
        </label>
    );
}

/** Pastille service/activité à bascule, avec icône du set park4night. */
function ToggleChip({ icon, label, checked, onToggle }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            className="tag"
            style={{
                padding: '8px 14px',
                cursor: 'pointer',
                border: `1px solid ${checked ? 'var(--p4n-blue)' : 'var(--border-1)'}`,
                background: checked ? 'var(--blue-50)' : 'var(--white)',
                color: checked ? 'var(--p4n-blue)' : 'var(--fg-3)',
            }}
        >
            <img src={icon} alt="" style={{ width: 18, height: 18, opacity: checked ? 1 : 0.45 }} />
            {label}
        </button>
    );
}

export default function PlaceEdit() {
    const { id } = useParams();
    const [place, setPlace] = useState(null);
    const [form, setForm] = useState(null);
    const [descLang, setDescLang] = useState(LANGS.includes(locale) ? locale : 'fr');
    const [saving, setSaving] = useState(false);
    const [flash, setFlash] = useState(null);
    // Incrémenté après un enregistrement de localisation pour re-monter la carte
    // (rafraîchit le repère d'origine et la distance de déplacement).
    const [locKey, setLocKey] = useState(0);

    const load = useCallback(() => {
        api.place(id).then((data) => {
            setPlace(data.place);
            setForm({
                name: data.place.name || '',
                route: data.place.route || '',
                ville: data.place.ville || '',
                code_postal: data.place.code_postal || '',
                region: data.place.region || '',
                latitude: Number.isFinite(data.place.latitude) ? data.place.latitude : null,
                longitude: Number.isFinite(data.place.longitude) ? data.place.longitude : null,
                date_fermeture: data.place.date_fermeture || '',
                prix_stationnement: data.place.prix_stationnement || '',
                prix_services: data.place.prix_services || '',
                nb_places: data.place.nb_places ?? '',
                hauteur_limite: data.place.hauteur_limite != null ? data.place.hauteur_limite.toFixed(2) : '0.00',
                borne: (data.place.borne || '').trim(),
                site_internet: data.place.site_internet || '',
                mail: data.place.mail || '',
                tel: data.place.tel || '',
                ...Object.fromEntries(LANGS.map((l) => [`description_${l}`, data.place[`description_${l}`] || ''])),
                flags: { ...data.place.flags },
            });
        }).catch((e) => setFlash({ type: 'error', text: e.message }));
    }, [id]);

    useEffect(load, [load]);

    if (!place || !form) return <Spinner />;

    const set = (field) => (e) => setForm({ ...form, [field]: e.target ? e.target.value : e });
    const toggleFlag = (flag) => setForm({ ...form, flags: { ...form.flags, [flag]: !form.flags[flag] } });

    const save = async () => {
        setSaving(true);
        setFlash(null);
        try {
            const payload = { ...form, nb_places: form.nb_places === '' ? null : Number(form.nb_places) };
            const result = await api.updatePlace(place.id, payload);

            // La localisation vit dans une colonne géométrique : enregistrée à part
            // (updateLocation) uniquement si le marqueur a bougé.
            const locationChanged = Number.isFinite(form.latitude) && Number.isFinite(form.longitude)
                && (form.latitude !== place.latitude || form.longitude !== place.longitude);
            if (locationChanged) {
                await api.updateLocation(place.id, form.latitude, form.longitude);
            }

            setFlash({
                type: 'success',
                text: t(result.modified || locationChanged ? 'success_update_place' : 'success_update_image'),
            });
            window.scrollTo({ top: 0, behavior: 'smooth' });
            if (locationChanged) {
                setLocKey((k) => k + 1);
                load();
            }
        } catch (e) {
            // Erreurs de validation renvoyées par l'API : message précis, sinon message générique
            const known = ['error_tel_invalid', 'error_mail_invalid'];
            setFlash({ type: 'error', text: t(known.includes(e.message) ? e.message : 'error_update') });
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div className="topbar" style={{ marginBottom: 16 }}>
                <div>
                    <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {place.type_code && (
                            <img src={`/img/access/icon_${place.type_code.toLowerCase()}.svg`} alt="" style={{ width: 36 }} />
                        )}
                        {place.title}
                    </h1>
                    <p className="page-subtitle">
                        <Link to="/" style={{ color: 'var(--p4n-blue)' }}>← {t('retour')}</Link>
                    </p>
                </div>
                <Button onClick={save} disabled={saving}>{t('enregistrer')}</Button>
            </div>
            <hr className="dashed-divider" />

            {flash && <Flash type={flash.type}>{flash.text}</Flash>}

            {/* Descriptions multilingues */}
            <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
                    <SectionTitle>{t('desc')}</SectionTitle>
                    <div className="toolbar">
                        {LANGS.map((code) => (
                            <button
                                key={code}
                                type="button"
                                onClick={() => setDescLang(code)}
                                style={{
                                    border: 'none', background: 'none', cursor: 'pointer', padding: 2,
                                    filter: descLang === code ? 'none' : 'grayscale(1) opacity(0.5)',
                                }}
                                title={code}
                            >
                                <img src={FLAG(code)} alt={code} style={{ width: 24, borderRadius: 3 }} />
                            </button>
                        ))}
                    </div>
                </div>
                <Textarea
                    value={form[`description_${descLang}`]}
                    onChange={set(`description_${descLang}`)}
                    rows={6}
                />
            </Card>

            {/* Infos utiles */}
            <Card>
                <SectionTitle>{t('infos_utiles')}</SectionTitle>
                <Field label={t('periode_ouverture')}>
                    <OpeningPeriod
                        value={form.date_fermeture}
                        onChange={(v) => setForm((f) => ({ ...f, date_fermeture: v }))}
                    />
                </Field>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0 24px', marginTop: 8 }}>
                    <Field label={t('prix_stationnement')}>
                        <Input value={form.prix_stationnement} onChange={set('prix_stationnement')} disabled={form.prix_stationnement === 'gratuit'} />
                        <SpecialValueCheckbox label={t('gratuit')} value={form.prix_stationnement} special="gratuit" onChange={set('prix_stationnement')} />
                    </Field>
                    <Field label={t('prix_services')}>
                        <Input value={form.prix_services} onChange={set('prix_services')} disabled={form.prix_services === 'gratuit'} />
                        <SpecialValueCheckbox label={t('gratuit')} value={form.prix_services} special="gratuit" onChange={set('prix_services')} />
                    </Field>
                    <Field label={t('nb_places')}>
                        <Input type="number" min="0" step="1" value={form.nb_places} onChange={set('nb_places')} />
                    </Field>
                    <Field label={t('hauteur_limite')}>
                        <Select value={form.hauteur_limite} onChange={set('hauteur_limite')}>
                            {HEIGHTS.map((h) => <option key={h} value={h}>{h === '0.00' ? '—' : `${h} m`}</option>)}
                        </Select>
                    </Field>
                    <Field label={t('borne')}>
                        <Select value={form.borne} onChange={set('borne')}>
                            {BORNES.map((code) => <option key={code} value={code}>{t(`B_${code}`)}</option>)}
                        </Select>
                    </Field>
                </div>
            </Card>

            {/* Titre & contact */}
            <Card>
                <SectionTitle>{t('titre')} & {t('contact')}</SectionTitle>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0 24px' }}>
                    <Field label={t('titre')}><Input value={form.name} onChange={set('name')} /></Field>
                    <Field label={t('site_internet')}>
                        <Input type="url" placeholder="https://example.com" value={form.site_internet} onChange={set('site_internet')} />
                    </Field>
                    <Field label={t('mail')}><Input type="email" value={form.mail} onChange={set('mail')} /></Field>
                    <Field label={t('tel')}>
                        <Input type="tel" placeholder="+33702030405" value={form.tel} onChange={set('tel')} />
                    </Field>
                </div>
            </Card>

            {/* Localisation : recherche + carte + adresse auto-remplie */}
            <Card>
                <LocationEditor
                    key={locKey}
                    place={place}
                    onChange={(p) => setForm((f) => ({ ...f, latitude: p.lat, longitude: p.lng }))}
                    onAddressResolved={(addr) => setForm((f) => ({
                        ...f,
                        route: addr.route || f.route,
                        ville: addr.ville || f.ville,
                        code_postal: addr.code_postal || f.code_postal,
                    }))}
                />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0 24px', marginTop: 16 }}>
                    <Field label={t('route')}><Input value={form.route} onChange={set('route')} /></Field>
                    <Field label={t('ville')}><Input value={form.ville} onChange={set('ville')} /></Field>
                    <Field label={t('code_postal')}><Input value={form.code_postal} onChange={set('code_postal')} /></Field>
                    <Field label={t('pays')}><Input value={place.pays || ''} disabled /></Field>
                </div>
            </Card>

            {/* Services */}
            <Card>
                <SectionTitle>{t('services')}</SectionTitle>
                <div className="toolbar">
                    {SERVICES.map((key) => (
                        <ToggleChip
                            key={key}
                            icon={`/img/services/service_${key}.svg`}
                            label={t(key)}
                            checked={!!form.flags[key]}
                            onToggle={() => toggleFlag(key)}
                        />
                    ))}
                </div>
            </Card>

            {/* Activités */}
            <Card>
                <SectionTitle>{t('activites')}</SectionTitle>
                <div className="toolbar">
                    {ACTIVITIES.map((key) => (
                        <ToggleChip
                            key={key}
                            icon={`/img/activities/activity_${key}.svg`}
                            label={t(key)}
                            checked={!!form.flags[key]}
                            onToggle={() => toggleFlag(key)}
                        />
                    ))}
                </div>
            </Card>

            {/* Photos */}
            <Card>
                <PhotoManager place={place} onChange={load} />
            </Card>

            <div className="toolbar" style={{ justifyContent: 'flex-end', marginTop: 24 }}>
                <Link to="/" className="btn btn--secondary">{t('annuler')}</Link>
                <Button onClick={save} disabled={saving}>{t('enregistrer')}</Button>
            </div>
        </>
    );
}
